import type {
  FurnitureDefinition,
  FurniturePlacement,
  FurnitureLayer,
} from '../../../core/domain/types';

export const ROOM_COLUMNS = 8;
export const ROOM_ROWS = 7;

export function placementSize(
  definition: FurnitureDefinition,
  rotation: FurniturePlacement['rotation'],
): { width: number; height: number } {
  return rotation === 90 || rotation === 270
    ? { width: definition.height, height: definition.width }
    : { width: definition.width, height: definition.height };
}

function occupiedCells(placement: FurniturePlacement, definition: FurnitureDefinition): string[] {
  const { width, height } = placementSize(definition, placement.rotation);
  const cells: string[] = [];
  for (let x = 0; x < width; x += 1) {
    for (let y = 0; y < height; y += 1) cells.push(`${placement.gridX + x}:${placement.gridY + y}`);
  }
  return cells;
}

export function isPlacementValid(
  candidate: FurniturePlacement,
  definition: FurnitureDefinition,
  placements: FurniturePlacement[],
  lookup: Map<string, FurnitureDefinition>,
): boolean {
  const { width, height } = placementSize(definition, candidate.rotation);
  if (
    candidate.gridX < 0 ||
    candidate.gridY < 0 ||
    candidate.gridX + width > ROOM_COLUMNS ||
    candidate.gridY + height > ROOM_ROWS
  ) {
    return false;
  }

  const occupied = new Set(occupiedCells(candidate, definition));
  return !placements.some((other) => {
    if (other.instanceId === candidate.instanceId || other.layer !== candidate.layer) return false;
    const otherDefinition = lookup.get(other.furnitureId);
    if (!otherDefinition) return false;
    return occupiedCells(other, otherDefinition).some((cell) => occupied.has(cell));
  });
}

export function findFirstSpace(
  definition: FurnitureDefinition,
  layer: FurnitureLayer,
  placements: FurniturePlacement[],
  lookup: Map<string, FurnitureDefinition>,
): FurniturePlacement | null {
  for (let y = 0; y < ROOM_ROWS; y += 1) {
    for (let x = 0; x < ROOM_COLUMNS; x += 1) {
      const candidate: FurniturePlacement = {
        instanceId: crypto.randomUUID(),
        furnitureId: definition.id,
        roomId: 'room_main',
        gridX: x,
        gridY: y,
        rotation: 0,
        layer,
      };
      if (isPlacementValid(candidate, definition, placements, lookup)) return candidate;
    }
  }
  return null;
}
